import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, SlidersHorizontal } from 'lucide-react';
import {
  RUM_TYPE_LABELS,
  RUM_TYPES,
  type RumType,
  type RumView,
  type SearchRumsInput,
} from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { DESKTOP_UP, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import {
  Bottle,
  Button,
  Corner,
  EmptyState,
  Input,
  ScoreMedallion,
  SheetHandle,
  Skeleton,
  Tag,
} from '@/shared/ui';
import styles from './cave.module.scss';

type Filters = Pick<SearchRumsInput, 'q' | 'country' | 'rumType' | 'minRating' | 'status'>;

/**
 * Écran 02 — la Cave (US-06).
 *
 * Le seul écran dont la structure change réellement d'un format à l'autre : les filtres
 * sont une feuille au pouce et un volet de 250 px en desktop. C'est donc le seul endroit
 * qui a besoin de `useMediaQuery` — tout le reste bascule en CSS.
 */
export function CaveScreen(): ReactNode {
  const api = useApi();
  const isDesktop = useMediaQuery(DESKTOP_UP);
  const [filters, setFilters] = useState<Filters>({});
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data, loading, error } = useAsync(
    () => api.rums.search({ ...filters, perPage: 48 }),
    [filters.q, filters.country, filters.rumType, filters.minRating, filters.status],
  );

  // Les pastilles « Pays » ne dépendent pas des filtres courants : les relire à chaque
  // pastille cliquée serait une requête pour rien.
  const countries = useAsync(() => api.rums.countries(), []);

  const activeCount = [filters.country, filters.rumType, filters.minRating, filters.status].filter(
    (value) => value !== undefined,
  ).length;

  const panel = (
    <FilterPanel
      filters={filters}
      countries={countries.data ?? []}
      onChange={setFilters}
      onClose={() => setSheetOpen(false)}
      resultCount={data?.total ?? 0}
      asSheet={!isDesktop}
    />
  );

  return (
    <div className={styles.layout}>
      <aside className={styles.sidePanel}>{panel}</aside>

      <div className={styles.content}>
        <header className={styles.head}>
          <h1>La Cave</h1>
          <span>
            {data ? `${data.total} bouteille${data.total > 1 ? 's' : ''}` : 'Chargement…'}
          </span>
          {/*
            L'écran « Nouvelle fiche » existait, sa route aussi, et rien n'y menait : on ne
            pouvait l'atteindre qu'en tapant l'URL. C'est ici sa place — on ajoute une
            bouteille en regardant la cave, pas depuis un menu.
          */}
          <Link className={styles.addRum} to="/rhums/nouveau">
            <Plus size={17} strokeWidth={1.5} aria-hidden="true" />
            Ajouter une bouteille
          </Link>
        </header>

        <div className={styles.searchRow}>
          <span className={styles.searchField}>
            <Search size={17} strokeWidth={1.6} aria-hidden="true" />
            <Input
              pill
              type="search"
              placeholder="Nom, distillerie…"
              value={filters.q ?? ''}
              onChange={(event) => setFilters({ ...filters, q: event.target.value || undefined })}
              aria-label="Rechercher dans la cave"
            />
          </span>

          <span className={styles.filterWrap}>
            <button
              type="button"
              className={styles.filterButton}
              aria-label="Filtrer la cave"
              onClick={() => setSheetOpen(true)}
            >
              <SlidersHorizontal size={19} strokeWidth={1.7} aria-hidden="true" />
            </button>
            {activeCount > 0 ? <span className={styles.activeCount}>{activeCount}</span> : null}
          </span>
        </div>

        {loading ? <Skeleton /> : null}
        {error ? <EmptyState>{error}</EmptyState> : null}
        {data && data.items.length === 0 ? (
          <EmptyState>Aucune bouteille ne correspond à ces critères.</EmptyState>
        ) : null}

        <ul className={styles.grid}>
          {data?.items.map((rum) => (
            <li key={rum.id}>
              <RumCard rum={rum} />
            </li>
          ))}
        </ul>
      </div>

      {!isDesktop && sheetOpen ? (
        <div
          className={styles.sheetBackdrop}
          onClick={() => setSheetOpen(false)}
          role="presentation"
        >
          <div className={styles.sheet} onClick={(event) => event.stopPropagation()}>
            <SheetHandle />
            {panel}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Une bouteille en vitrine : silhouette sur sa flaque de lumière, note en médaillon. */
function RumCard({ rum }: { rum: RumView }): ReactNode {
  return (
    <Link to={`/rhums/${rum.id}`} className={styles.card}>
      <span className={styles.showcase}>
        {rum.photoUrl ? (
          <img src={rum.photoUrl} alt="" loading="lazy" />
        ) : (
          <Bottle size="md" muted={rum.isMasked} />
        )}
        {/* Le coin doré ne se pose que sur le coup de cœur du club. */}
        {rum.averageScore !== null && rum.averageScore >= 90 ? <Corner /> : null}
        <span className={styles.cardScore}>
          <ScoreMedallion score={rum.averageScore} />
        </span>
      </span>

      <span className={styles.cardBody}>
        <strong>{rum.name}</strong>
        <span>
          {rum.isMasked
            ? 'Fiche masquée'
            : [rum.country, rum.rumType ? RUM_TYPE_LABELS[rum.rumType] : null]
                .filter(Boolean)
                .join(' · ')}
        </span>
      </span>
    </Link>
  );
}

interface FilterPanelProps {
  filters: Filters;
  /** Les pays réellement en cave, du plus représenté au moins représenté. */
  countries: readonly string[];
  onChange: (filters: Filters) => void;
  onClose: () => void;
  resultCount: number;
  asSheet: boolean;
}

function FilterPanel({
  filters,
  countries,
  onChange,
  onClose,
  resultCount,
  asSheet,
}: FilterPanelProps): ReactNode {
  const toggle = <K extends keyof Filters>(key: K, value: Filters[K]): void => {
    onChange({ ...filters, [key]: filters[key] === value ? undefined : value });
  };

  // Le pays filtré reste proposé même s'il quitte la liste — une dernière bouteille passée
  // à l'aveugle, par exemple. Sans cela, sa pastille disparaîtrait et le filtre actif
  // deviendrait impossible à retirer autrement qu'en réinitialisant tout.
  const shownCountries =
    filters.country && !countries.includes(filters.country)
      ? [filters.country, ...countries]
      : countries;

  return (
    <div className={styles.filters}>
      <header className={styles.filtersHead}>
        <h2>{asSheet ? 'Filtrer la cave' : 'Affiner'}</h2>
        <Button variant="ghost" onClick={() => onChange({ q: filters.q })}>
          Réinitialiser
        </Button>
      </header>

      {/*
        Les pays viennent de la cave et non d'une liste figée : celui qu'un membre ajoute en
        créant sa fiche apparaît ici dès la première bouteille. Pas de troncature — masquer
        des options est précisément ce qu'on corrige, et le panneau défile.
      */}
      <fieldset className={styles.group}>
        <legend>Pays</legend>
        <div className={styles.chips}>
          {shownCountries.map((country) => (
            <Tag
              key={country}
              selected={filters.country === country}
              onClick={() => toggle('country', country)}
            >
              {country}
            </Tag>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend>Type</legend>
        <div className={styles.chips}>
          {RUM_TYPES.map((type) => (
            <Tag
              key={type}
              selected={filters.rumType === type}
              onClick={() => toggle('rumType', type as RumType)}
            >
              {RUM_TYPE_LABELS[type]}
            </Tag>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend>Note du club</legend>
        <div className={styles.chips}>
          {[70, 80, 90].map((threshold) => (
            <Tag
              key={threshold}
              selected={filters.minRating === threshold}
              onClick={() => toggle('minRating', threshold)}
            >
              ≥ {(threshold / 10).toFixed(1).replace('.', ',')}
            </Tag>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.group}>
        <legend>Statut</legend>
        <div className={styles.chips}>
          <Tag selected={filters.status === 'TASTED'} onClick={() => toggle('status', 'TASTED')}>
            Dégusté
          </Tag>
          <Tag
            selected={filters.status === 'TO_TASTE'}
            onClick={() => toggle('status', 'TO_TASTE')}
          >
            À déguster
          </Tag>
        </div>
      </fieldset>

      {asSheet ? (
        <Button block onClick={onClose}>
          Voir {resultCount} rhum{resultCount > 1 ? 's' : ''}
        </Button>
      ) : null}
    </div>
  );
}
